/**
 * Typed environment access — works under three runtimes during the migration:
 *  - Vite (dev/build): reads `import.meta.env.VITE_*` (statically inlined by Vite).
 *  - Vitest: Vite-powered, so the Vite branch is used (tests unaffected).
 *  - Next.js: reads `process.env.NEXT_PUBLIC_*` (statically inlined by Next).
 *
 * Runtime is chosen once at module load: `import.meta.env` exists only under
 * Vite/Vitest, and is `undefined` under Next/webpack. The two builders are lazy
 * so the non-active branch is never evaluated (avoids `undefined.VITE_X` under
 * Next and `process is not defined` under Vite). Each var is accessed with a
 * static literal on both sides so the bundlers can inline it.
 *
 * All `VITE_*` are client-public and map 1:1 to `NEXT_PUBLIC_*`. Build flags
 * (`DEV`/`PROD`/`MODE`) map to `process.env.NODE_ENV`.
 *
 * Migration note: this replaces direct `import.meta.env.*` reads across the app
 * (It. 1 codemod). Once Vite is removed (It. 8), the Vite branch can be dropped.
 */

const viteEnv = (
  import.meta as unknown as { env?: Record<string, string | undefined> }
).env
const isVite = Boolean(viteEnv)

interface AppEnv {
  API_URL: string | undefined
  KEYCLOAK_URL: string | undefined
  KEYCLOAK_REALM: string | undefined
  KEYCLOAK_CLIENT_ID: string | undefined
  KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID: string | undefined
  /** Keycloak alias of the Google identity provider (kc_idp_hint); unset hides the Google SSO button. */
  KEYCLOAK_GOOGLE_IDP_ALIAS: string | undefined
  /** Keycloak alias of the Microsoft identity provider; unset keeps the pill as "coming soon". */
  KEYCLOAK_MICROSOFT_IDP_ALIAS: string | undefined
  COOKIE_DOMAIN: string | undefined
  CONTACT_EMAIL: string | undefined
  GOOGLE_MAPS_API_KEY: string | undefined
  MAPBOX_ACCESS_TOKEN: string | undefined
  MAP_LIGHT_URL: string | undefined
  MAP_DARK_URL: string | undefined
  CMS_URL: string | undefined
  CMS_ACCESS_TOKEN: string | undefined
  CRISP_WEBSITE_ID: string | undefined
  CHATWOOT_BASE_URL: string | undefined
  CHATWOOT_WEBSITE_TOKEN: string | undefined
  DISCORD_INVITATION_LINK: string | undefined
  GITLAB_LINK: string | undefined
  FACEBOOK_LINK: string | undefined
  LINKEDIN_LINK: string | undefined
  MASTODON_LINK: string | undefined
  MERCURE_PUBLIC_URL: string | undefined
  PUBLIC_POSTHOG_KEY: string | undefined
  PUBLIC_POSTHOG_HOST: string | undefined
  SENTRY_DSN: string | undefined
  /**
   * Canonical public origin of this app (mirrors `app/_lib/site.ts` SITE_URL).
   * Used to build absolute deep links (e.g. QR-code labels).
   */
  SITE_URL: string | undefined
  WEB_URL: string | undefined
  AUTH_OIDC_DEBUG: string | undefined
}

const buildViteEnv = (): AppEnv => ({
  API_URL: import.meta.env.VITE_API_URL,
  KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL,
  KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID: import.meta.env
    .VITE_KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID,
  KEYCLOAK_GOOGLE_IDP_ALIAS: import.meta.env.VITE_KEYCLOAK_GOOGLE_IDP_ALIAS,
  KEYCLOAK_MICROSOFT_IDP_ALIAS: import.meta.env
    .VITE_KEYCLOAK_MICROSOFT_IDP_ALIAS,
  COOKIE_DOMAIN: import.meta.env.VITE_COOKIE_DOMAIN,
  CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL,
  GOOGLE_MAPS_API_KEY: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  MAPBOX_ACCESS_TOKEN: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
  MAP_LIGHT_URL: import.meta.env.VITE_MAP_LIGHT_URL,
  MAP_DARK_URL: import.meta.env.VITE_MAP_DARK_URL,
  CMS_URL: import.meta.env.VITE_CMS_URL,
  CMS_ACCESS_TOKEN: import.meta.env.VITE_CMS_ACCESS_TOKEN,
  CRISP_WEBSITE_ID: import.meta.env.VITE_CRISP_WEBSITE_ID,
  CHATWOOT_BASE_URL: import.meta.env.VITE_CHATWOOT_BASE_URL,
  CHATWOOT_WEBSITE_TOKEN: import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN,
  DISCORD_INVITATION_LINK: import.meta.env.VITE_DISCORD_INVITATION_LINK,
  GITLAB_LINK: import.meta.env.VITE_GITLAB_LINK,
  FACEBOOK_LINK: import.meta.env.VITE_FACEBOOK_LINK,
  LINKEDIN_LINK: import.meta.env.VITE_LINKEDIN_LINK,
  MASTODON_LINK: import.meta.env.VITE_MASTODON_LINK,
  MERCURE_PUBLIC_URL: import.meta.env.VITE_MERCURE_PUBLIC_URL,
  PUBLIC_POSTHOG_KEY: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
  PUBLIC_POSTHOG_HOST: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  SITE_URL: import.meta.env.VITE_SITE_URL,
  WEB_URL: import.meta.env.VITE_WEB_URL,
  AUTH_OIDC_DEBUG: import.meta.env.VITE_AUTH_OIDC_DEBUG,
})

const buildNextEnv = (): AppEnv => ({
  API_URL: process.env.NEXT_PUBLIC_API_URL,
  KEYCLOAK_URL: process.env.NEXT_PUBLIC_KEYCLOAK_URL,
  KEYCLOAK_REALM: process.env.NEXT_PUBLIC_KEYCLOAK_REALM,
  KEYCLOAK_CLIENT_ID: process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID,
  KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID:
    process.env.NEXT_PUBLIC_KEYCLOAK_TOKEN_EXCHANGE_CLIENT_ID,
  KEYCLOAK_GOOGLE_IDP_ALIAS: process.env.NEXT_PUBLIC_KEYCLOAK_GOOGLE_IDP_ALIAS,
  KEYCLOAK_MICROSOFT_IDP_ALIAS:
    process.env.NEXT_PUBLIC_KEYCLOAK_MICROSOFT_IDP_ALIAS,
  COOKIE_DOMAIN: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
  CONTACT_EMAIL: process.env.NEXT_PUBLIC_CONTACT_EMAIL,
  GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  MAPBOX_ACCESS_TOKEN: process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
  MAP_LIGHT_URL: process.env.NEXT_PUBLIC_MAP_LIGHT_URL,
  MAP_DARK_URL: process.env.NEXT_PUBLIC_MAP_DARK_URL,
  CMS_URL: process.env.NEXT_PUBLIC_CMS_URL,
  CMS_ACCESS_TOKEN: process.env.NEXT_PUBLIC_CMS_ACCESS_TOKEN,
  CRISP_WEBSITE_ID: process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID,
  CHATWOOT_BASE_URL: process.env.NEXT_PUBLIC_CHATWOOT_BASE_URL,
  CHATWOOT_WEBSITE_TOKEN: process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN,
  DISCORD_INVITATION_LINK: process.env.NEXT_PUBLIC_DISCORD_INVITATION_LINK,
  GITLAB_LINK: process.env.NEXT_PUBLIC_GITLAB_LINK,
  FACEBOOK_LINK: process.env.NEXT_PUBLIC_FACEBOOK_LINK,
  LINKEDIN_LINK: process.env.NEXT_PUBLIC_LINKEDIN_LINK,
  MASTODON_LINK: process.env.NEXT_PUBLIC_MASTODON_LINK,
  MERCURE_PUBLIC_URL: process.env.NEXT_PUBLIC_MERCURE_PUBLIC_URL,
  PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_PUBLIC_POSTHOG_KEY,
  PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_PUBLIC_POSTHOG_HOST,
  SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  WEB_URL: process.env.NEXT_PUBLIC_WEB_URL,
  AUTH_OIDC_DEBUG: process.env.NEXT_PUBLIC_AUTH_OIDC_DEBUG,
})

/**
 * The API origin for the host the browser is actually standing on.
 *
 * On a dedicated space, the browser is on the tenant's own domain (vega.demo.communo.app,
 * veganetwork.fr) and the API lives at its derived twin, `api.` + that host — that is what
 * lets the backend's HostNetworkResolver read the right scope from the request. The configured
 * NEXT_PUBLIC_API_URL points at the shared platform's API and, used from a tenant domain,
 * would silently serve the shared platform: every list empty of the tenant's own data, with
 * nothing to say why.
 *
 * Exported for its tests; runtime callers go through `env.API_URL`.
 *
 * @param configured the build-time API URL (the shared platform's)
 * @param windowHost `window.location.host`, or null outside a browser
 */
export const apiUrlForHost = (
  configured: string | undefined,
  windowHost: string | null
): string | undefined => {
  if (!configured || !windowHost) return configured

  const platform = platformFrontHostOf(configured)
  if (!platform) return configured

  // On the platform itself, or anywhere local (yarn dev against a remote API is a normal
  // workflow), the configured URL is the right one.
  if (!isTenant(windowHost, platform)) return configured

  // Any other host is a tenant domain: same scheme, derived twin, no port games — tenant
  // hosts are only ever reached through the proxy on 443.
  return `https://api.${windowHost.toLowerCase()}`
}

/**
 * The platform's front host, derived from an API URL by dropping its `api.` label — the
 * naming convention this deployment already uses (api.demo.communo.app ↔ demo.communo.app).
 *
 * Null when there is nothing to derive from: no URL configured, or one that does not follow
 * the convention. Every caller then falls back to treating the current host as the platform,
 * which is the safe reading — it never invents a tenant.
 */
const platformFrontHostOf = (configured: string | undefined): string | null => {
  if (!configured) return null
  try {
    const apiHost = new URL(configured).host

    return apiHost.startsWith('api.') ? apiHost.slice('api.'.length) : null
  } catch {
    return null
  }
}

/** Whether this host is a dedicated space's, rather than the platform's or a local one. */
const isTenant = (windowHost: string, platformFrontHost: string): boolean => {
  const host = windowHost.toLowerCase()

  return (
    host !== platformFrontHost &&
    !host.startsWith('localhost') &&
    !host.startsWith('127.') &&
    !host.startsWith('[::1]')
  )
}

const builtEnv: AppEnv = isVite ? buildViteEnv() : buildNextEnv()

/**
 * The shared platform's front origin, regardless of the host the browser stands on.
 *
 * Derived from the *configured* API URL (never the runtime-derived one): on a tenant domain
 * `env.API_URL` points at the tenant's own twin, but the platform front is exactly what a
 * closing dedicated space needs to send its owner back to — the tenant host stops existing
 * during that mutation, so nothing derived from `window.location` can name the destination.
 */
export const platformFrontUrl = (): string | undefined => {
  const platform = platformFrontHostOf(builtEnv.API_URL)

  return platform === null ? undefined : `https://${platform}`
}

/**
 * Whether a host is a dedicated space's own domain rather than the platform's.
 *
 * Exported for its tests, like {@see apiUrlForHost}; runtime callers use `isTenantHost()`.
 *
 * @param configured the build-time API URL (the shared platform's)
 * @param windowHost `window.location.host`, or null outside a browser
 */
export const isTenantHostFor = (
  configured: string | undefined,
  windowHost: string | null
): boolean => {
  const platform = platformFrontHostOf(configured)

  return (
    platform !== null && windowHost !== null && isTenant(windowHost, platform)
  )
}

/**
 * Whether the browser is standing on a dedicated space's own domain.
 *
 * Asked by anything that would otherwise send a visitor somewhere the platform owns. The
 * marketing site is the case that matters: a dedicated space has none, so bouncing an
 * unauthenticated visitor there drops them out of the space they asked for — they typed
 * their network's address and land on Communo's shop window.
 *
 * Outside a browser it answers false, which keeps every caller on the platform's behaviour.
 */
export const isTenantHost = (): boolean =>
  isTenantHostFor(
    builtEnv.API_URL,
    typeof window === 'undefined' ? null : window.location.host
  )

export const env: AppEnv = {
  ...builtEnv,
  API_URL: apiUrlForHost(
    builtEnv.API_URL,
    typeof window === 'undefined' ? null : window.location.host
  ),
}

/** Build-time flags (was `import.meta.env.DEV/PROD/MODE`). */
export const isDev: boolean = isVite
  ? Boolean(viteEnv?.DEV)
  : process.env.NODE_ENV !== 'production'
export const isProd: boolean = isVite
  ? Boolean(viteEnv?.PROD)
  : process.env.NODE_ENV === 'production'
export const mode: string = isVite
  ? (viteEnv?.MODE ?? 'development')
  : (process.env.NODE_ENV ?? 'development')
